import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    IconButton,
    FormControlLabel,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowDown,
    faArrowUp,
    faChevronDown,
    faChevronRight,
    faFloppyDisk,
    faFolderPlus,
    faPlus,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import { settingsApi } from 'Pages/Admin/Settings/settingsApi';
import { ArticleView } from './calculatorApi';

type LmNodeKind = 'Group' | 'Feature' | 'Comment';

interface LmNodeDto {
    kind: LmNodeKind;
    comment: string | null;
    xmlId: string | null;
    domaene: string | null;
    optional: string | null;
    abhaengig: string | null;
    condition: string | null;
    titel: string | null;
    titelLm: string | null;
    titelReq: string | null;
    doc: string | null;
    docLm: string | null;
    docReq: string | null;
    children: LmNodeDto[];
}

interface LmgDocument {
    stylesheetHref: string | null;
    leadingComments: string[];
    root: LmNodeDto;
    trailingComments: string[];
}

interface LmFileInfo {
    file: string;
    shortRef: string;
}

const emptyNode = (kind: LmNodeKind): LmNodeDto => ({
    kind,
    comment: null, xmlId: null, domaene: null, optional: null, abhaengig: null, condition: null,
    // Leer statt Platzhalter: einen vorgegebenen Text muss man erst löschen, bevor man tippt.
    titel: '',
    titelLm: null, titelReq: null, doc: null, docLm: null, docReq: null,
    children: [],
});

/**
 * Der Leistungsmerkmal-Editor.
 *
 * **Warum es ihn braucht.** Der Server konnte diese Dateien immer schon lesen und schreiben, und
 * die Integritätsprüfung meldet seit jeher „fehlende Leistungsbeschreibung" — nur schließen ließ
 * sich die Lücke nirgends. Eine Warnung, der keine Handlung folgt, wird nach kurzer Zeit
 * überlesen.
 *
 * **Warum drei Textfelder je Knoten.** Dasselbe Merkmal steht im Angebot beschreibend („das System
 * zeichnet auf") und im Leistungsverzeichnis fordernd („die Aufzeichnung muss …"). Beide Formen im
 * selben Feld zu führen zwänge dazu, eine davon jedes Mal von Hand umzuformulieren. Leer bleiben
 * die Varianten der Normalfall — dann gilt der Standardtext.
 *
 * **Warum Kommentare mitlaufen.** Die Dateien tragen gepflegte Anmerkungen aus Jahren. Ein Editor,
 * der sie beim Speichern verliert, ist schlimmer als keiner.
 */
export const LvEditor: React.FC<{
    articles: ArticleView[];
    /** Vorauswahl aus dem Katalog — der Sprung soll nicht in einem leeren Formular landen. */
    initialArticle?: string | null;
}> = ({ articles, initialArticle }) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const context = useApplicationContext();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [dir, setDir] = useState<string>(initialArticle ?? '');
    const [files, setFiles] = useState<LmFileInfo[]>([]);
    const [file, setFile] = useState<string>('');
    const [doc, setDoc] = useState<LmgDocument | null>(null);
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    /**
     * Welche Fassung die Vorschau zeigt.
     *
     * Ohne Vorschau bearbeitet man einen Baum und sieht nie, was daraus wird — und gerade beim LV
     * ist die Ausgabe der eigentliche Gegenstand. Der Umschalter macht zugleich sichtbar, wofür es
     * die zweite Textfassung überhaupt gibt.
     */
    const [mode, setMode] = useState<'lm' | 'req'>('lm');

    // Der Sprung aus dem Katalog kann auch dann kommen, wenn hier schon etwas gewählt war.
    useEffect(() => {
        if (initialArticle) setDir(initialArticle);
    }, [initialArticle]);

    /** Nur Artikel ohne Leistungstext zeigen — die Arbeitsliste der Pflege. */
    const [onlyMissing, setOnlyMissing] = useState(false);

    /**
     * Die Auswahlliste zeigt den Zustand, statt ihn erst nach dem Klick zu verraten.
     *
     * `missingLeistung` steht in jeder Artikelzeile und wurde hier zunächst nicht genutzt — man
     * wählte blind und landete bei den meisten Artikeln vor „Neue Datei", ohne je zu erfahren,
     * welche denn einen Text haben.
     */
    const withDir = useMemo(
        () => articles
            .filter((a) => !a.missingArticleDir)
            .filter((a) => !onlyMissing || a.missingLeistung)
            .sort((a, b) => Number(b.missingLeistung) - Number(a.missingLeistung)
                || a.articleNumber.localeCompare(b.articleNumber)),
        [articles, onlyMissing],
    );

    const stats = useMemo(() => {
        const relevant = articles.filter((a) => !a.missingArticleDir);
        return {
            total: relevant.length,
            withText: relevant.filter((a) => !a.missingLeistung).length,
            withoutDir: articles.length - relevant.length,
        };
    }, [articles]);

    const loadFiles = useCallback(async (target: string) => {
        setFiles([]); setFile(''); setDoc(null); setError(null);
        if (!target) return;
        try {
            const r = await http.get<LmFileInfo[]>(`/Calculator/edit/lm/${encodeURIComponent(target)}`);
            setFiles(r.data);
            if (r.data.length === 1) setFile(r.data[0].file);
        } catch {
            setError(`Für ${target} gibt es noch keine Leistungsdatei. Über „Neue Datei" lässt sich `
                + 'eine anlegen — üblich ist lm.xml.');
        }
    }, [http]);

    useEffect(() => { loadFiles(dir); }, [dir, loadFiles]);

    useEffect(() => {
        if (!dir || !file) { setDoc(null); return; }
        setError(null);
        http.get<LmgDocument>(`/Calculator/edit/lm/${encodeURIComponent(dir)}/${encodeURIComponent(file)}`)
            .then((r) => { setDoc(r.data); setDirty(false); })
            .catch(() => setError('Die Datei konnte nicht geladen werden.'));
    }, [dir, file, http]);

    const save = async () => {
        if (!doc) return;
        setBusy(true);
        try {
            await http.put(`/Calculator/edit/lm/${encodeURIComponent(dir)}/${encodeURIComponent(file)}`, doc);
            setDirty(false);
            enqueueSnackbar('Leistungsmerkmale gespeichert.', { variant: 'success' });
        } catch {
            enqueueSnackbar('Speichern fehlgeschlagen — fehlt die Berechtigung?', { variant: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const createFile = async () => {
        const name = window.prompt('Dateiname (z. B. lm.xml oder lm.tetra.xml)', 'lm.xml');
        if (!name || !dir) return;
        try {
            await http.post(`/Calculator/edit/lm/${encodeURIComponent(dir)}/${encodeURIComponent(name)}`, undefined, null);
            await loadFiles(dir);
            setFile(name);
            enqueueSnackbar(`${name} angelegt.`, { variant: 'success' });
        } catch {
            enqueueSnackbar('Anlegen fehlgeschlagen — gibt es die Datei schon?', { variant: 'warning' });
        }
    };

    /** Ersetzt einen Knoten an seinem Pfad; der Baum bleibt unveränderlich. */
    const replaceAt = (node: LmNodeDto, path: number[], update: (n: LmNodeDto) => LmNodeDto | null): LmNodeDto => {
        if (path.length === 0) return update(node) ?? node;
        const [head, ...rest] = path;
        const children = [...node.children];

        if (rest.length === 0) {
            const next = update(children[head]);
            if (next === null) children.splice(head, 1);
            else children[head] = next;
        } else {
            children[head] = replaceAt(children[head], rest, update);
        }

        return { ...node, children };
    };

    const mutate = (path: number[], update: (n: LmNodeDto) => LmNodeDto | null) => {
        if (!doc) return;
        setDoc({ ...doc, root: replaceAt(doc.root, path, update) });
        setDirty(true);
    };

    /**
     * Verschiebt einen Knoten unter seinen Geschwistern.
     *
     * Die Reihenfolge ist im LV inhaltlich: sie bestimmt, in welcher Folge der Kunde die Merkmale
     * liest. Ohne diese Möglichkeit müsste man zum Umsortieren löschen und neu tippen.
     */
    const move = (path: number[], delta: number) => {
        if (!doc || path.length === 0) return;
        const parentPath = path.slice(0, -1);
        const index = path[path.length - 1];

        setDoc({
            ...doc,
            root: replaceAt(doc.root, parentPath, (parent) => {
                const children = [...parent.children];
                const target = index + delta;
                if (target < 0 || target >= children.length) return parent;
                [children[index], children[target]] = [children[target], children[index]];
                return { ...parent, children };
            }),
        });
        setDirty(true);
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 380 }}
                    options={withDir}
                    getOptionLabel={(a) => `${a.articleNumber} — ${a.beschreibung || ''}`}
                    isOptionEqualToValue={(a, b) => a.articleNumber === b.articleNumber}
                    value={withDir.find((a) => a.articleNumber === dir) ?? null}
                    onChange={(_, a) => setDir(a?.articleNumber ?? '')}
                    renderInput={(params) => <TextField {...params} label="Artikel" />}
                    renderOption={(props, a) => (
                        <li {...props} key={a.articleNumber}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%' }}>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
                                    {a.articleNumber}
                                </Typography>
                                <Typography variant="caption" noWrap sx={{ flexGrow: 1, color: 'text.secondary' }}>
                                    {a.beschreibung}
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color={a.missingLeistung ? 'warning' : 'success'}
                                    label={a.missingLeistung ? 'kein Text' : 'Text da'}
                                    sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                                />
                            </Stack>
                        </li>
                    )}
                />

                <FormControlLabel
                    control={(
                        <Switch
                            size="small"
                            checked={onlyMissing}
                            onChange={(e) => setOnlyMissing(e.target.checked)}
                        />
                    )}
                    label={<Typography variant="body2">nur ohne Text</Typography>}
                />

                {files.length > 0 && (
                    <TextField
                        select
                        size="small"
                        label="Datei"
                        value={file}
                        onChange={(e) => setFile(e.target.value)}
                        sx={{ minWidth: 220 }}
                    >
                        {files.map((f) => (
                            <MenuItem key={f.file} value={f.file}>
                                {f.file}{f.shortRef ? ` (${f.shortRef})` : ' — Vorgabe'}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {dir && (
                    <Button
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faFolderPlus} />}
                        onClick={createFile}
                    >
                        Neue Datei
                    </Button>
                )}

                <Box sx={{ flexGrow: 1 }} />

                {doc && (
                    <Button
                        variant="contained"
                        size="small"
                        disabled={!dirty || busy}
                        startIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                        onClick={save}
                    >
                        Speichern
                    </Button>
                )}
            </Stack>

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {stats.withText} von {stats.total} Artikeln mit Artikel-Verzeichnis haben einen
                Leistungstext
                {stats.withoutDir > 0 && ` · ${stats.withoutDir} Artikel ohne Verzeichnis können keinen tragen`}
            </Typography>

            {error && <Alert severity="info">{error}</Alert>}

            {!dir && (
                <Alert severity="info">
                    Wähle einen Artikel. Leistungsmerkmale liegen je Artikel-Verzeichnis als
                    {' '}<code>lm*.xml</code> und speisen sowohl den Angebotstext als auch das
                    Leistungsverzeichnis.
                </Alert>
            )}

            {doc && (
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1.35fr 1fr' } }}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2, maxHeight: '68vh', overflow: 'auto',
                            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.03 : 0.02),
                        }}
                    >
                        <LmNode node={doc.root} path={[]} depth={0} onChange={mutate} onMove={move} />
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, maxHeight: '68vh', overflow: 'auto' }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Vorschau</Typography>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={mode}
                                onChange={(_, v) => v && setMode(v)}
                            >
                                <ToggleButton value="lm">Angebot</ToggleButton>
                                <ToggleButton value="req">Leistungsverzeichnis</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                        <Divider sx={{ mb: 1.5 }} />
                        <LvPreview node={doc.root} mode={mode} depth={0} />
                    </Paper>
                </Box>
            )}

            {doc && (doc.leadingComments.length > 0 || doc.trailingComments.length > 0) && (
                <Alert severity="info" variant="outlined">
                    Die Datei enthält {doc.leadingComments.length + doc.trailingComments.length}{' '}
                    Dokument-Kommentare. Sie bleiben beim Speichern erhalten, werden hier aber nicht
                    bearbeitet.
                </Alert>
            )}
        </Stack>
    );
};

/**
 * Die Vorschau — so liest es der Kunde.
 *
 * Gerendert wird dieselbe Auswahllogik wie im Server: je Modus gewinnt die passende Fassung, sonst
 * gilt der Standardtext. Bewusst im Client und nicht über einen Aufruf: die Vorschau soll beim
 * Tippen mitlaufen, nicht erst nach dem Speichern.
 */
const LvPreview: React.FC<{ node: LmNodeDto; mode: 'lm' | 'req'; depth: number }> = ({ node, mode, depth }) => {
    const theme = useTheme();
    if (node.kind === 'Comment') return null;

    const titel = (mode === 'req' ? node.titelReq : node.titelLm) || node.titel || '';
    const doc = (mode === 'req' ? node.docReq : node.docLm) || node.doc || '';
    const isGroup = node.kind === 'Group';

    return (
        <Box sx={{ ml: depth > 1 ? 2 : 0, mb: isGroup ? 1.5 : 0.75 }}>
            {titel && (
                isGroup ? (
                    <Typography
                        variant={depth === 0 ? 'subtitle1' : 'subtitle2'}
                        sx={{ fontWeight: 600, mt: depth === 0 ? 0 : 1.5 }}
                    >
                        {titel}
                    </Typography>
                ) : (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                        <Box sx={{ color: 'text.disabled', fontSize: 11, lineHeight: 1.6 }}>•</Box>
                        <Typography variant="body2">
                            {titel}
                            {node.optional && (
                                <Typography component="span" variant="caption" sx={{ color: 'warning.main' }}>
                                    {' '}(optional)
                                </Typography>
                            )}
                        </Typography>
                    </Stack>
                )
            )}

            {doc && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block', color: 'text.secondary',
                        ml: isGroup ? 0 : 2.5, mt: 0.25, whiteSpace: 'pre-wrap',
                    }}
                >
                    {doc}
                </Typography>
            )}

            {!titel && !doc && (
                <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>
                    (ohne Text — erscheint im {mode === 'req' ? 'Leistungsverzeichnis' : 'Angebot'} nicht)
                </Typography>
            )}

            {node.children.map((c, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <LvPreview key={`${c.kind}-${i}`} node={c} mode={mode} depth={depth + 1} />
            ))}
        </Box>
    );
};

/** Ein Knoten im Baum: Gruppe mit Kindern, Merkmal als Blatt, Kommentar unveränderlich. */
const LmNode: React.FC<{
    node: LmNodeDto;
    path: number[];
    depth: number;
    onChange: (path: number[], update: (n: LmNodeDto) => LmNodeDto | null) => void;
    onMove: (path: number[], delta: number) => void;
}> = ({ node, path, depth, onChange, onMove }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(depth < 2);
    const [expanded, setExpanded] = useState(false);

    if (node.kind === 'Comment') {
        return (
            <Box sx={{ ml: depth * 2, my: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    {/* Kommentare bleiben erhalten, aber unangetastet — sie tragen oft Historie. */}
                    {node.comment}
                </Typography>
            </Box>
        );
    }

    const isGroup = node.kind === 'Group';
    const accent = isGroup ? theme.palette.primary.main : theme.palette.info.main;

    const set = (field: keyof LmNodeDto) => (value: string) =>
        onChange(path, (n) => ({ ...n, [field]: value || null }));

    return (
        <Box sx={{ ml: depth === 0 ? 0 : 2, mt: 1 }}>
            <Paper
                variant="outlined"
                sx={{ p: 1.25, borderLeft: `3px solid ${accent}`, bgcolor: 'background.paper' }}
            >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {isGroup && (
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} fontSize={11} />
                        </IconButton>
                    )}

                    <TextField
                        size="small"
                        variant="standard"
                        value={node.titel ?? ''}
                        onChange={(e) => set('titel')(e.target.value)}
                        placeholder={isGroup ? 'Gruppentitel' : 'Merkmal'}
                        sx={{ flexGrow: 1 }}
                    />

                    {node.domaene && <Chip size="small" variant="outlined" label={node.domaene} />}
                    {node.optional && (
                        <Tooltip title={node.optional === '2' ? 'optional und bedingt' : 'Zusatz'}>
                            <Chip size="small" color="warning" variant="outlined" label="optional" />
                        </Tooltip>
                    )}

                    <Button size="small" onClick={() => setExpanded(!expanded)}>
                        {expanded ? 'Weniger' : 'Texte'}
                    </Button>

                    {isGroup && (
                        <>
                            <Tooltip title="Merkmal hinzufügen">
                                <IconButton
                                    size="small"
                                    onClick={() => onChange(path, (n) => ({
                                        ...n, children: [...n.children, emptyNode('Feature')],
                                    }))}
                                >
                                    <FontAwesomeIcon icon={faPlus} fontSize={11} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Untergruppe hinzufügen">
                                <IconButton
                                    size="small"
                                    onClick={() => onChange(path, (n) => ({
                                        ...n, children: [...n.children, emptyNode('Group')],
                                    }))}
                                >
                                    <FontAwesomeIcon icon={faFolderPlus} fontSize={11} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}

                    {depth > 0 && (
                        <>
                            <Tooltip title="Nach oben">
                                <IconButton size="small" onClick={() => onMove(path, -1)}>
                                    <FontAwesomeIcon icon={faArrowUp} fontSize={11} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Nach unten">
                                <IconButton size="small" onClick={() => onMove(path, 1)}>
                                    <FontAwesomeIcon icon={faArrowDown} fontSize={11} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}

                    {depth > 0 && (
                        <Tooltip title="Entfernen">
                            <IconButton size="small" color="error" onClick={() => onChange(path, () => null)}>
                                <FontAwesomeIcon icon={faTrash} fontSize={11} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                <Collapse in={expanded} unmountOnExit>
                    <Divider sx={{ my: 1.25 }} />
                    <Stack spacing={1.5}>
                        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
                            Leer lassen heißt: es gilt der Standardtext. Eigene Fassungen nur dort,
                            wo Angebot und Leistungsverzeichnis wirklich anders klingen müssen.
                        </Alert>

                        <TextField
                            size="small" fullWidth multiline minRows={2}
                            label="Beschreibung (Standard)"
                            value={node.doc ?? ''}
                            onChange={(e) => set('doc')(e.target.value)}
                        />

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                            <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    Angebot — beschreibend
                                </Typography>
                                <TextField
                                    size="small" fullWidth label="Titel"
                                    value={node.titelLm ?? ''}
                                    onChange={(e) => set('titelLm')(e.target.value)}
                                />
                                <TextField
                                    size="small" fullWidth multiline minRows={2} label="Beschreibung"
                                    value={node.docLm ?? ''}
                                    onChange={(e) => set('docLm')(e.target.value)}
                                />
                            </Stack>

                            <Stack spacing={1} sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    Leistungsverzeichnis — fordernd
                                </Typography>
                                <TextField
                                    size="small" fullWidth label="Titel"
                                    value={node.titelReq ?? ''}
                                    onChange={(e) => set('titelReq')(e.target.value)}
                                />
                                <TextField
                                    size="small" fullWidth multiline minRows={2} label="Beschreibung"
                                    value={node.docReq ?? ''}
                                    onChange={(e) => set('docReq')(e.target.value)}
                                />
                            </Stack>
                        </Stack>

                        <Stack direction="row" spacing={1.5}>
                            <TextField
                                size="small" label="Domäne" sx={{ width: 180 }}
                                value={node.domaene ?? ''}
                                onChange={(e) => set('domaene')(e.target.value)}
                                helperText="Filter, erbt an Kinder"
                            />
                            <TextField
                                select size="small" label="Optional" sx={{ width: 180 }}
                                value={node.optional ?? ''}
                                onChange={(e) => set('optional')(e.target.value)}
                            >
                                <MenuItem value="">(nein)</MenuItem>
                                <MenuItem value="1">Zusatz</MenuItem>
                                <MenuItem value="2">optional und bedingt</MenuItem>
                            </TextField>
                            <TextField
                                size="small" label="Bedingung" sx={{ width: 180 }}
                                value={node.condition ?? ''}
                                onChange={(e) => set('condition')(e.target.value)}
                            />
                        </Stack>
                    </Stack>
                </Collapse>
            </Paper>

            {isGroup && (
                <Collapse in={open} unmountOnExit>
                    {node.children.map((child, i) => (
                        <LmNode
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${child.kind}-${i}`}
                            node={child}
                            path={[...path, i]}
                            depth={depth + 1}
                            onChange={onChange}
                            onMove={onMove}
                        />
                    ))}
                </Collapse>
            )}
        </Box>
    );
};

export default LvEditor;
